import {
  DIFFICULTIES,
  MANIFEST_FILE,
  MV_FILE_EXTENSION_KEY,
  SongPackArchive,
  chartFileName,
  type LoadedSongPack,
} from '../songpack/index.js';
import type { AssetName } from './SongRepository.js';

/**
 * Turning a Song Pack into stored assets, and back.
 *
 * The rule this module exists to enforce is **verbatim storage**: what comes out
 * of an archive goes into storage unchanged, so what goes back out is the
 * producer's own bytes rather than this runtime's re-serialisation of them. A
 * chart that has been parsed and re-stringified is content-equivalent and byte-
 * different, and byte-different is enough to make "did the round trip change
 * anything?" unanswerable.
 *
 * There is exactly one exception, and it is deliberate: attaching a music video
 * has to add `extensions.mvFile` to the manifest, so the manifest of an
 * MV-bearing pack is rewritten. A pack with no video is never touched.
 */

/** Assets, and the bytes each one holds. */
export type AssetMap = Map<AssetName, Blob>;

/**
 * Splits a validated pack into storable assets.
 *
 * `manifest` and the three charts are re-read from the archive as **raw bytes**
 * rather than taken from the parsed pack — the parsed form has already lost the
 * producer's key order and number formatting, and export would silently invent
 * new ones.
 *
 * The music and cover are taken from the parsed pack, which already holds their
 * raw bytes, so they are not inflated twice.
 */
export function splitPack(archiveBytes: Uint8Array, pack: LoadedSongPack): AssetMap {
  const archive = SongPackArchive.open(archiveBytes);
  const wanted = [MANIFEST_FILE, ...DIFFICULTIES.map(chartFileName)];
  const raw = archive.extract(wanted);

  const assets: AssetMap = new Map();
  assets.set('manifest', blobOf(raw.get(MANIFEST_FILE), 'application/json'));
  assets.set('easy', blobOf(raw.get(chartFileName('easy')), 'application/json'));
  assets.set('normal', blobOf(raw.get(chartFileName('normal')), 'application/json'));
  assets.set('hard', blobOf(raw.get(chartFileName('hard')), 'application/json'));
  assets.set('music', blobOf(pack.music, 'audio/wav'));
  if (pack.cover !== null) assets.set('cover', blobOf(pack.cover, 'image/png'));

  const video = readVideo(archive, archiveBytes, pack);
  if (video !== null) assets.set('video', video);
  return assets;
}

/**
 * The bundled video as a `Blob`, by the cheapest route available.
 *
 * A video is written stored rather than deflated — an MP4 is already
 * compressed — so its bytes normally sit in the archive verbatim and a `Blob`
 * can be built from a view over them. That matters at the sizes an MV reaches:
 * inflating a 200 MB entry and then copying the result costs two 200 MB
 * allocations on the JavaScript heap, and this costs none.
 *
 * A deflated video still works; it just goes the expensive way.
 */
function readVideo(
  archive: SongPackArchive,
  archiveBytes: Uint8Array,
  pack: LoadedSongPack,
): Blob | null {
  const entry = pack.videoEntry;
  if (entry === null) return null;
  const type = videoMimeFor(entry);

  const range = archive.storedRange(entry);
  if (range !== null) {
    return new Blob([blobPart(archiveBytes.subarray(range.start, range.end))], { type });
  }
  // Already inflated by the loader when it was asked to include the video;
  // otherwise inflate it now.
  const bytes = pack.video?.bytes ?? archive.extract([entry]).get(entry);
  return bytes === undefined ? null : new Blob([blobPart(bytes)], { type });
}

/**
 * Adds a music video to a pack's assets, rewriting the manifest to name it.
 *
 * The video arrives as a `Blob` and is stored as one, never read into memory:
 * on the CREATE path it is the `File` the player chose, which the browser
 * already has on disk. A 200 MB MV therefore costs nothing here beyond a
 * reference.
 *
 * The manifest is rewritten by parsing it, adding one entry to the array
 * `extensions` already is, and re-serialising. Every other key keeps its value
 * and its position, because `JSON.parse` preserves insertion order and this only
 * ever appends.
 */
export async function attachVideo(
  assets: AssetMap,
  video: Blob,
  entryName: string,
): Promise<AssetMap> {
  const manifest = assets.get('manifest');
  if (manifest === undefined) throw new Error('The pack has no manifest to attach a video to.');
  const source = JSON.parse(await manifest.text()) as Record<string, unknown>;

  const existing = Array.isArray(source['extensions'])
    ? (source['extensions'] as { key?: unknown; value?: unknown }[])
    : [];
  // Version 1 declares `extensions` as an array of {key, value} pairs, in Unity,
  // in Python and in this runtime's own validator. Writing an object here would
  // be rejected by all three, so the array shape is not a stylistic choice.
  source['extensions'] = [
    ...existing.filter((property) => property.key !== MV_FILE_EXTENSION_KEY),
    { key: MV_FILE_EXTENSION_KEY, value: entryName },
  ];

  const next = new Map(assets);
  // Two-space indent, matching what the Python engine writes.
  next.set('manifest', new Blob([JSON.stringify(source, null, 2)], { type: 'application/json' }));
  next.set('video', video);
  return next;
}

/** Removes the video and its manifest reference. */
export async function detachVideo(assets: AssetMap): Promise<AssetMap> {
  const manifest = assets.get('manifest');
  if (manifest === undefined) return assets;
  const source = JSON.parse(await manifest.text()) as Record<string, unknown>;
  const existing = Array.isArray(source['extensions'])
    ? (source['extensions'] as { key?: unknown }[])
    : [];
  source['extensions'] = existing.filter((property) => property.key !== MV_FILE_EXTENSION_KEY);

  const next = new Map(assets);
  next.set('manifest', new Blob([JSON.stringify(source, null, 2)], { type: 'application/json' }));
  next.delete('video');
  return next;
}

/** MIME type for a bundled video, from its entry name. */
export function videoMimeFor(entryName: string): string {
  return entryName.toLowerCase().endsWith('.webm') ? 'video/webm' : 'video/mp4';
}

/**
 * The entry name a chosen video file should be stored under.
 *
 * The player's file name is not used. It would put arbitrary text into an
 * archive entry name — the one place in a Song Pack where arbitrary text is a
 * path-traversal question — and the manifest already carries the real title.
 */
export function videoEntryNameFor(fileName: string): string {
  return fileName.toLowerCase().endsWith('.webm') ? 'video.webm' : 'video.mp4';
}

function blobOf(bytes: Uint8Array | undefined, type: string): Blob {
  if (bytes === undefined) throw new Error('A required Song Pack entry was missing.');
  // No defensive `slice()`. The `Blob` constructor copies what it is given into
  // the browser's own storage, so a view over the archive is safe — and the
  // extra copy would be a second full-size allocation on the JavaScript heap for
  // every asset, which for a music video is the whole problem.
  return new Blob([blobPart(bytes)], { type });
}

/**
 * Narrows a `Uint8Array` to the one shape `Blob` accepts.
 *
 * `Uint8Array` defaults to "backed by an ArrayBuffer *or* a SharedArrayBuffer",
 * and `Blob` refuses the shared case. Nothing in a Song Pack is ever shared —
 * the bytes come from `fetch`, a `File`, or fflate — so this narrows a type
 * rather than converting a value, and it does so here instead of at each of the
 * half-dozen call sites.
 */
function blobPart(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  return bytes as Uint8Array<ArrayBuffer>;
}
